import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { geohashForLocation } from 'geofire-common';
import { create } from 'zustand';
import { analyticsClient } from '@/lib';
import { db } from '@/lib/firebase';
import { settingsService } from '@/services/settingsService';
import { addProfilePhoto, removeProfilePhoto } from '@/services/storageService';
import { userService } from '@/services/userService';
import { UserProfile, type ProfilePhoto } from '@/types';
import { normalizePhotos, reorderPhotos as reorderPhotoList } from '@/utils';

interface OnboardingState {
  // Form data
  birthday: string;
  height: number | undefined;
  pronouns: string;
  selectedGenders: string[];
  selectedOrientations: string[];
  selectedLanguages: string[];
  selectedEthnicities: string[];
  city: string;
  country: string;
  latitude: number | undefined;
  longitude: number | undefined;
  selectedInterests: string[];
  selectedSpots: string[];
  selectedOpenTo: string[];
  selectedRelationshipTypes: string[];
  relationshipStatus: string;
  selectedIntimacy: string[];
  locationAllowed: boolean;
  notificationsEnabled: boolean;
  intentions: string[];
  takingABreak: boolean;
  onboardingStep: number;
  bio: string;
  photos: ProfilePhoto[];

  // UI state
  uploading: boolean;
  error: string | null;
  loading: boolean;
  isLoadingData: boolean;

  // User context
  uid: string;
  userName: string;
  userEmail: string;
}

interface OnboardingActions {
  // Setters
  setBirthday: (birthday: string) => void;
  setHeight: (height: number | undefined) => void;
  setPronouns: (pronouns: string) => void;
  setSelectedGenders: (genders: string[]) => void;
  setSelectedOrientations: (orientations: string[]) => void;
  setSelectedLanguages: (languages: string[]) => void;
  setSelectedEthnicities: (ethnicities: string[]) => void;
  setCity: (city: string) => void;
  setCountry: (country: string) => void;
  setLatitude: (latitude: number | undefined) => void;
  setLongitude: (longitude: number | undefined) => void;
  setSelectedInterests: (interests: string[]) => void;
  setSelectedSpots: (spots: string[]) => void;
  setSelectedOpenTo: (openTo: string[]) => void;
  setSelectedRelationshipTypes: (relationshipTypes: string[]) => void;
  setRelationshipStatus: (relationshipStatus: string) => void;
  setSelectedIntimacy: (intimacy: string[]) => void;
  setLocationAllowed: (locationAllowed: boolean) => void;
  setNotificationsEnabled: (notificationsEnabled: boolean) => void;
  setIntentions: (intentions: string[]) => void;
  setTakingABreak: (takingABreak: boolean) => void;
  setOnboardingStep: (onboardingStep: number) => void;
  setBio: (bio: string) => void;
  setPhotos: (photos: ProfilePhoto[]) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setIsLoadingData: (isLoadingData: boolean) => void;

  // Actions
  setUserContext: (uid: string, userName: string, userEmail: string) => void;
  setUserName: (userName: string) => void;
  fetchUserData: () => Promise<void>;
  uploadPhoto: (blob: Blob, filename: string) => Promise<void>;
  removePhoto: (index: number) => Promise<void>;
  reorderPhotos: (from: number, to: number) => Promise<void>;
  saveCurrentData: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  reset: () => void;
}

type OnboardingStore = OnboardingState & OnboardingActions;

/** Firestore rejects undefined field values — strip them before any write. */
const omitUndefined = <T extends Record<string, unknown>>(data: T) =>
  Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined)) as Partial<T>;

/** Base64 data URLs belong in Storage, not Firestore (1 MiB doc limit). */
const photosForFirestore = (photos: ProfilePhoto[]) =>
  photos.filter(photo => Boolean(photo.url) && !photo.url.startsWith('data:'));

/** Many actions need a valid uid; fail fast with a clear message instead of a Storage permission error. */
const requireUserId = (uid: string, setError: (error: string | null) => void): void => {
  if (!uid) {
    const message = 'Missing user session. Please sign in again.';
    setError(message);
    throw new Error(message);
  }
};

const normalizeStringArray = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const normalizeEthnicities = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const initialState: Omit<OnboardingState, 'uid' | 'userName' | 'userEmail'> = {
  birthday: '',
  height: undefined,
  pronouns: '',
  selectedGenders: [],
  selectedOrientations: [],
  selectedLanguages: [],
  selectedEthnicities: [],
  city: 'Berlin',
  country: 'Germany',
  latitude: 52.52,
  longitude: 13.405,
  selectedInterests: [],
  selectedSpots: [],
  selectedOpenTo: [],
  selectedRelationshipTypes: [],
  relationshipStatus: '',
  selectedIntimacy: [],
  locationAllowed: false,
  notificationsEnabled: false,
  intentions: [],
  takingABreak: false,
  onboardingStep: 0,
  bio: '',
  photos: [],
  uploading: false,
  error: null,
  loading: false,
  isLoadingData: false,
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  ...initialState,
  uid: '',
  userName: '',
  userEmail: '',

  setUserContext: (uid, userName, userEmail) => set({ uid, userName, userEmail }),

  setUserName: userName => set({ userName }),

  fetchUserData: async () => {
    const { uid } = get();
    if (!uid) return;

    set({ isLoadingData: true });

    try {
      const userRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userRef);
      const settings = userDoc.exists() ? await settingsService.getSettings(uid) : null;

      if (userDoc.exists()) {
        const userData = userDoc.data() as UserProfile;

        // Only populate store if user has started onboarding (has any onboarding data)
        if (
          userData.birthday ||
          userData.gender ||
          userData.city ||
          userData.interests ||
          userData.photos
        ) {
          set({
            birthday: userData.birthday || '',
            height: userData.height,
            pronouns: userData.pronouns ?? undefined,
            selectedGenders: normalizeStringArray(userData.gender),
            selectedOrientations: normalizeStringArray(userData.orientation),
            selectedLanguages: userData.languages || [],
            selectedEthnicities: normalizeEthnicities(userData.ethnicity),
            city: userData.city || 'Berlin',
            country: userData.country || 'Germany',
            latitude: userData.latitude,
            longitude: userData.longitude,
            selectedInterests: userData.interests || [],
            selectedSpots: userData.spots || [],
            selectedOpenTo: userData.openTo || [],
            selectedRelationshipTypes: userData.relationshipTypes || [],
            relationshipStatus: userData.relationshipStatus || '',
            selectedIntimacy: userData.intimacy || [],
            locationAllowed: settings?.locationAllowed ?? false,
            notificationsEnabled: settings?.notificationsEnabled ?? false,
            intentions: userData.intentions || [],
            takingABreak: userData.takingABreak ?? false,
            onboardingStep: userData.onboardingStep ?? 0,
            bio: userData.bio || '',
            photos: normalizePhotos(userData.photos),
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      // Don't set error state here - just use defaults if fetch fails
    } finally {
      set({ isLoadingData: false });
    }
  },

  setBirthday: birthday => set({ birthday }),
  setHeight: height => set({ height }),
  setPronouns: pronouns => set({ pronouns }),
  setSelectedGenders: selectedGenders => set({ selectedGenders }),
  setSelectedOrientations: selectedOrientations => set({ selectedOrientations }),
  setSelectedLanguages: selectedLanguages => set({ selectedLanguages }),
  setSelectedEthnicities: selectedEthnicities => set({ selectedEthnicities }),
  setCity: city => set({ city }),
  setCountry: country => set({ country }),
  setLatitude: latitude => set({ latitude }),
  setLongitude: longitude => set({ longitude }),
  setSelectedInterests: selectedInterests => set({ selectedInterests }),
  setSelectedSpots: selectedSpots => set({ selectedSpots }),
  setSelectedOpenTo: selectedOpenTo => set({ selectedOpenTo }),
  setSelectedRelationshipTypes: selectedRelationshipTypes => set({ selectedRelationshipTypes }),
  setRelationshipStatus: relationshipStatus => set({ relationshipStatus }),
  setSelectedIntimacy: selectedIntimacy => set({ selectedIntimacy }),
  setLocationAllowed: locationAllowed => set({ locationAllowed }),
  setNotificationsEnabled: notificationsEnabled => set({ notificationsEnabled }),
  setIntentions: intentions => set({ intentions }),
  setTakingABreak: takingABreak => set({ takingABreak }),
  setOnboardingStep: onboardingStep => set({ onboardingStep }),
  setBio: bio => set({ bio }),
  setPhotos: photos => set({ photos }),
  setError: error => set({ error }),
  setLoading: loading => set({ loading }),
  setIsLoadingData: isLoadingData => set({ isLoadingData }),

  uploadPhoto: async (blob, filename) => {
    const { uid, photos, setError } = get();
    requireUserId(uid, setError);

    set({ uploading: true, error: null });
    try {
      const updated = await addProfilePhoto(uid, photos, blob, filename);
      set({ photos: updated });
      await userService.updateProfile(uid, { photos: updated });
    } catch (err) {
      console.error(err);
      set({
        error: err instanceof Error ? err.message : 'Failed to upload photo. Please try again.',
      });
    } finally {
      set({ uploading: false });
    }
  },

  removePhoto: async indexToDrop => {
    const { uid, photos, setError } = get();
    requireUserId(uid, setError);

    set({ uploading: true, error: null });
    try {
      const updated = await removeProfilePhoto(photos, indexToDrop);
      set({ photos: updated });
      await userService.updateProfile(uid, { photos: updated });
    } catch (err) {
      console.error(err);
      set({ error: 'Failed to remove photo. Please try again.' });
    } finally {
      set({ uploading: false });
    }
  },

  reorderPhotos: async (from, to) => {
    const { uid, photos, setError } = get();
    requireUserId(uid, setError);

    set({ uploading: true, error: null });
    try {
      const updated = reorderPhotoList(photos, from, to);
      set({ photos: updated });
      await userService.updateProfile(uid, { photos: updated });
    } catch (err) {
      console.error(err);
      set({ error: 'Failed to reorder photos. Please try again.' });
    } finally {
      set({ uploading: false });
    }
  },

  saveCurrentData: async () => {
    const {
      uid,
      setError,
      userName,
      birthday,
      selectedGenders,
      selectedOrientations,
      pronouns,
      city,
      country,
      latitude,
      longitude,
      selectedLanguages,
      selectedEthnicities,
      height,
      selectedInterests,
      selectedSpots,
      selectedOpenTo,
      selectedRelationshipTypes,
      relationshipStatus,
      selectedIntimacy,
      locationAllowed,
      notificationsEnabled,
      intentions,
      takingABreak,
      onboardingStep,
      bio,
      photos,
    } = get();

    requireUserId(uid, setError);

    try {
      set({ error: null });
      const userRef = doc(db, 'users', uid);
      const geohash =
        typeof latitude === 'number' && typeof longitude === 'number'
          ? geohashForLocation([latitude, longitude])
          : undefined;
      const profileData = omitUndefined({
        uid,
        name: userName,
        birthday,
        gender: selectedGenders,
        orientation: selectedOrientations,
        pronouns,
        city: city.trim(),
        country: country.trim(),
        latitude,
        longitude,
        ...(geohash ? { geohash } : {}),
        languages: selectedLanguages,
        ...(selectedEthnicities.length > 0 ? { ethnicity: selectedEthnicities } : {}),
        height,
        interests: selectedInterests,
        ...(selectedSpots.length > 0 ? { spots: selectedSpots } : {}),
        openTo: selectedOpenTo,
        relationshipTypes: selectedRelationshipTypes,
        relationshipStatus,
        intimacy: selectedIntimacy,
        intentions,
        takingABreak,
        onboardingStep,
        bio: bio.trim(),
        ...(photosForFirestore(photos).length > 0 ? { photos: photosForFirestore(photos) } : {}),
      });

      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        throw new Error(
          'User profile document is missing. Signup should create it before onboarding.'
        );
      }
      await updateDoc(userRef, profileData);

      await settingsService.updateSettings(uid, { locationAllowed, notificationsEnabled });
    } catch (err: unknown) {
      console.error('Failed to save data:', err);
      set({ error: 'Failed to save your progress. Please try again.' });
      throw err;
    }
  },

  completeOnboarding: async () => {
    set({ loading: true, error: null });

    try {
      await get().saveCurrentData();

      const { uid } = get();
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        onboarded: true,
        status: 'active',
        onboardedAt: new Date().toISOString(),
      });

      analyticsClient.capture('onboarding_completed', {
        onboarded_at: new Date().toISOString(),
      });
    } catch (err: unknown) {
      console.error(err);
      set({ error: err instanceof Error ? err.message : 'Failed to update onboarding data.' });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  reset: () =>
    set({ ...initialState, uid: get().uid, userName: get().userName, userEmail: get().userEmail }),
}));
